import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import {
  type EntityRefValue,
  type IssueAttachment,
  type IssueFieldMedia,
  IssueViewDataDocument,
} from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useIssueTypesWithFields } from "#app/hooks/issue-view/use-issue-types-with-fields";
import type {
  IssueViewStage,
  CustomFieldDef,
} from "#app/components/issue-view/issue-view-types";
import type { AccessLevel } from "#app/lib/utils/access-level";
import type { Omittable } from "@salestrak/ui";

export function useIssueViewData(issueId: string | undefined) {
  const { customFields: workspaceCustomFields } = useWorkspaceData();
  const issueTypes = useIssueTypesWithFields();
  const variables = useMemo(() => ({ issueId: issueId! }), [issueId]);
  const { data, loading, error, refetch } = useQuery(IssueViewDataDocument, {
    variables,
    skip: !issueId,
    fetchPolicy: "cache-and-network",
    returnPartialData: true,
    errorPolicy: "all",
  });

  const rawIssue = data?.issue as
    | (Record<string, unknown> & Omittable<{
        stage?: IssueViewStage | null;
        assignees?: EntityRefValue[];
        values?: Record<string, unknown> | null;
        formCount?: number | null;
        attachments?: IssueAttachment[];
        fieldMedia?: IssueFieldMedia[];
        workflowId?: string | null;
        addressId?: string | null;
        viewerAccessLevel?: AccessLevel | null;
        viewerCanEdit?: boolean;
        address?: { id: string; addressId: string; addressLine1?: string | null; city?: string | null; stateProvince?: string | null; postalCode?: string | null; wholeAddress?: string | null; latLng?: { lat: number; lng: number } | null } | null;
      }>)
    | null
    | undefined;

  const issue = rawIssue ?? null;
  const stage = rawIssue?.stage;
  const assignees = rawIssue?.assignees;
  const values = rawIssue?.values;
  const formCount = rawIssue?.formCount;
  const attachments = rawIssue?.attachments;
  const fieldMedia = rawIssue?.fieldMedia;
  const workflowId = rawIssue?.workflowId;
  const viewerAccessLevel = rawIssue?.viewerAccessLevel;
  const viewerCanEdit = rawIssue?.viewerCanEdit;
  const address = rawIssue ? (rawIssue.address ?? null) : undefined;

  const customFields: CustomFieldDef[] = workspaceCustomFields;

  return { issue, stage, assignees, values, formCount, attachments, fieldMedia, workflowId, viewerAccessLevel, viewerCanEdit, address, issueTypes, customFields, loading, error, refetch };
}
